import { useState } from "react";

import type { LabTurn } from "@/lib/labModel";
import type { LabRunModel } from "@/lib/labRunModel";
import {
  EVENT_STATUS_LABELS,
  EVENT_STATUS_TONES,
  readPromptCall,
  type PromptCall,
} from "@/lib/previewPromptCall";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { PromptProvenanceLayers } from "@/ui/preview/PromptProvenanceLayers";

import { NOTHING, Section } from "./LabTurnSummary";

/**
 * Prompt assembly tab: for each prompt call of the turn, how its prompt was
 * composed (the provenance layer stack with origin chips, including clinical
 * memory). Each call loads its layers only when its own "Show assembly"
 * control is pressed, so a turn with many calls does not read them all; layer
 * text stays behind the component's own explicit control.
 */
export function LabAssemblyTab({
  projectId,
  previewRunRef,
  turn,
}: {
  projectId: string;
  previewRunRef: string;
  turn: LabTurn;
  model: LabRunModel;
  onSelectTurn: (turn: number) => void;
}) {
  const [shown, setShown] = useState<ReadonlySet<string>>(new Set());
  const calls: PromptCall[] = [];
  let unreadable = 0;
  for (const record of turn.promptCalls) {
    const reading = readPromptCall(record.record);
    if (reading.kind === "readable") {
      calls.push(reading.call);
    } else {
      unreadable += 1;
    }
  }
  function toggle(ref: string) {
    setShown((current) => {
      const next = new Set(current);
      if (!next.delete(ref)) {
        next.add(ref);
      }
      return next;
    });
  }
  return (
    <Section title="Prompt assembly">
      {calls.length === 0 && unreadable === 0 ? NOTHING : null}
      {unreadable > 0 ? (
        <p className="text-sm text-muted-foreground">
          {unreadable} call{unreadable === 1 ? "" : "s"} could not be read by
          this build
        </p>
      ) : null}
      <ul className="space-y-3">
        {calls.map((call) => {
          const open = shown.has(call.eventRef);
          const name = call.label ?? "Prompt call";
          return (
            <li
              key={call.eventRef}
              className="space-y-2 rounded-md border border-border p-2 text-sm"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span dir="auto" className="font-medium">
                  {name}
                </span>
                {call.callType !== null ? (
                  <Badge tone="outline">
                    <span dir="ltr">{call.callType}</span>
                  </Badge>
                ) : null}
                <Badge tone={EVENT_STATUS_TONES[call.status]}>
                  {EVENT_STATUS_LABELS[call.status]}
                </Badge>
                {call.modelIdentity?.model ? (
                  <span dir="ltr" className="text-xs text-muted-foreground">
                    {call.modelIdentity.model}
                  </span>
                ) : null}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                aria-expanded={open}
                aria-label={`${open ? "Hide" : "Show"} assembly: ${name}`}
                onClick={() => toggle(call.eventRef)}
              >
                {open ? "Hide assembly" : "Show assembly"}
              </Button>
              {open ? (
                <PromptProvenanceLayers
                  call={call}
                  projectId={projectId}
                  previewRunRef={previewRunRef}
                />
              ) : null}
            </li>
          );
        })}
      </ul>
    </Section>
  );
}
